/* eslint no-param-reassign: 0 */
import axios from 'axios';
import Vue from 'vue';
import Cookies from 'js-cookie';
import * as types from '../mutation-types';
import authAPI from '../../api/auth';
import CodesAPI from '../../api/codes';
import createAxios from '../../helper/APIHelper';
import actionCable from '../../helper/actionCable';
import { setUser, getHeaderExpiry, clearCookiesOnLogout } from '../utils/api';
import { DEFAULT_REDIRECT_URL } from '../../constants';
import {decrypt} from '../../../widget/helpers/utils';
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import AccountAPI from "../../api/account";
import ApiClient from "../../api/ApiClient";

const state = {
  currentUser: {
    id: null,
    account_id: null,
    channel: null,
    email: null,
    name: null,
    provider: null,
    uid: null,
    subscription: {
      state: null,
      expiry: null,
    },
  },
  currentAccountId: null,
  records: [],
};

// Boot-time hydration: seed auth store from cookies so filters (e.g., "Mine")
// have a user id before validityCheck runs.
const seedUserFromCookies = store => {
  try {
    const cookieUser = authAPI.getCurrentUser();
    if (cookieUser && cookieUser.id) {
      store.commit(types.default.SET_CURRENT_USER, cookieUser);
      if (cookieUser.account_id) {
        store.commit(types.default.SET_CURRENT_ACCOUNT_ID, cookieUser.account_id);
      }
    }
  } catch (e) {
    // ignore
  }
};

// getters
export const getters = {
  isLoggedIn($state) {
    return !!$state.currentUser.id;
  },

  getCurrentUserID(_state) {
    var cookieUser = Cookies.getJSON('user');
    return cookieUser.id;
  },

  getUISettings(_state) {
    return _state.currentUser.ui_settings || {};
  },

  getCurrentUserAvailability(_state) {
    const { accounts = [] } = _state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === _state.currentAccountId
    );
    return currentAccount.availability;
  },

  getCurrentAccountId(_state) {
    return _state.currentAccountId;
  },

  getCurrentRole(_state) {
    const { accounts = [] } = _state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === _state.currentAccountId
    );
    return currentAccount.role;
  },

  getCurrentUser(_state) {
    return _state.currentUser;
  },

  getCodes(_state) {
    return _state.records;
  },


};

// actions
export const actions = {
  hydrateUserFromCookies({ commit }) {
    seedUserFromCookies({ commit });
  },

  async getCodes({ commit }) {
    try {
      const response = await CodesAPI.get();
      commit(types.default.SET_CODES, response.data.payload);
    } catch (error) {
      // Ignore error
    }
  },

  login({ commit, dispatch }, credentials) {
    return new Promise((resolve, reject) => {
      authAPI
        .login(credentials)
        .then(async response => {
          // Set axios defaults from auth headers immediately
          const h = response?.headers || {};
          if (h['access-token'] && h.client && h.uid) {
            axios.defaults.headers.common['access-token'] = h['access-token'];
            axios.defaults.headers.common.client = h.client;
            axios.defaults.headers.common.uid = h.uid;
            if (h.expiry) axios.defaults.headers.common.expiry = h.expiry;
            if (h['token-type']) axios.defaults.headers.common['token-type'] = h['token-type'];
          }
          // Fetch fresh user payload so cookies/state are populated even if
          // /auth/sign_in returns a minimal body. Awaiting this ensures the
          // user cookie is written before we navigate.
          try {
            await dispatch('validityCheck');
          } catch (e) {
            // Continue; router will trigger another validityCheck on load.
          }

          // Redirect straight to the user's dashboard when possible; fall back
          // to /app so the router guard can finish hydration.
          const user = authAPI.getCurrentUser();
          window.axios = createAxios(axios);
          actionCable.init(Vue);

          if (user && user.account_id) {
            window.location = `/app/accounts/${user.account_id}/dashboard`;
          } else {
            window.location = DEFAULT_REDIRECT_URL;
          }
          resolve();
        })
        .catch(error => {
          reject(error);
        });
    });
  },
  async validityCheck(context) {
    try {
      const response = await authAPI.validityCheck();

      const payload = response.data.payload.data;
      const existingAccounts = context.state.currentUser?.accounts || [];
      const cookieUser = Cookies.getJSON('user') || {};
      const cookieAccounts = cookieUser.accounts || [];

      if (payload) {
        const incoming = payload.accounts || [];
        // Merge accounts from payload + existing + cookie, dedup by id
        const mergedMap = new Map();
        [...incoming, ...existingAccounts, ...cookieAccounts].forEach(acc => {
          if (acc && acc.id) mergedMap.set(acc.id, acc);
        });
        payload.accounts = Array.from(mergedMap.values());

        // Guardrail: preserve account_id even if validate_token omits it
        const primaryAccount = payload.accounts.find(acc => acc && acc.id);
        const fallbackAccountId =
          payload.account_id ||
          cookieUser.account_id ||
          context.state.currentUser?.account_id ||
          primaryAccount?.id;
        if (!payload.account_id && fallbackAccountId) {
          payload.account_id = fallbackAccountId;
        }
      }
      payload.client_secret = decrypt(payload.client_secret);
      payload.client_id = decrypt(payload.client_id);
      payload.google_api_key = decrypt(payload.google_api_key);

      setUser(payload, getHeaderExpiry(response), {
        setUserInSDK: true,
      });
      context.commit(types.default.SET_CURRENT_USER, payload);
    } catch (error) {
      if (error?.response?.status === 401) {
        console.log("LogoutLog: validity check");
        clearCookiesOnLogout();
      }
    }
  },

  async setUserData(context, id){
    try {
      const response = await authAPI.setUserData(id);
      const existingAccounts = context.state.currentUser?.accounts || [];
      const incomingAccounts = response.data.accounts || [];
      // Merge existing + incoming so we never collapse the list to a single account
      const mergedMap = new Map();
      [...incomingAccounts, ...existingAccounts].forEach(acc => {
        if (acc && acc.id) mergedMap.set(acc.id, acc);
      });

      const payload = {
        ...response.data,
        accounts: Array.from(mergedMap.values()),
      };

      setUser(payload, getHeaderExpiry(response), {
        setUserInSDK: true,
      });
      context.commit(types.default.SET_CURRENT_USER, payload);
      // Optional: refresh full memberships; failures ignored
      context.dispatch('validityCheck').catch(() => {});
    } catch (error) {
      if (error?.response?.status === 401) {
        console.log("LogoutLog: validity check");
        clearCookiesOnLogout();
      }
    }
  },

  setUser({ commit, dispatch }) {
    if (authAPI.isLoggedIn()) {
      seedUserFromCookies({ commit });
      dispatch('validityCheck');
      dispatch('getCodes');
    } else {
      commit(types.default.CLEAR_USER);
    }
  },
  setUserInfo({ commit, dispatch }, id) {
    if (authAPI.isLoggedIn()) {
      dispatch('setUserData', id);
    } else {
      commit(types.default.CLEAR_USER);
    }
  },
  logout({ commit }) {
    commit(types.default.CLEAR_USER);
  },

  updateProfile: async ({ commit }, params) => {
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await authAPI.profileUpdate(params);

      const payload = response.data;
      payload.client_secret = decrypt(payload.client_secret);
      payload.client_id = decrypt(payload.client_id);
      payload.google_api_key = decrypt(payload.google_api_key);

      setUser(payload, getHeaderExpiry(response));
      commit(types.default.SET_CURRENT_USER, payload);
    } catch (error) {
      throw error;
    }
  },

  updateUISettings: async ({ commit }, params) => {
    try {
      commit(types.default.SET_CURRENT_USER_UI_SETTINGS, params);
      const response = await authAPI.updateUISettings(params);

      const payload = response.data;
      payload.client_secret = decrypt(payload.client_secret);
      payload.client_id = decrypt(payload.client_id);
      payload.google_api_key = decrypt(payload.google_api_key);

      setUser(payload, getHeaderExpiry(response));
      commit(types.default.SET_CURRENT_USER, payload);
    } catch (error) {
      // Ignore error
    }
  },

  updateAvailability: async ({ commit, dispatch }, params) => {
    try {
      const response = await authAPI.updateAvailability(params);
      const userData = response.data;

      const payload = userData;
      payload.client_secret = decrypt(payload.client_secret);
      payload.client_id = decrypt(payload.client_id);
      payload.google_api_key = decrypt(payload.google_api_key);

      const { id } = userData;
      setUser(payload, getHeaderExpiry(response));
      commit(types.default.SET_CURRENT_USER, payload);
      dispatch('agents/updatePresence', { [id]: params.availability });
    } catch (error) {
      // Ignore error
    }
  },

  setCurrentAccountId({ commit }, accountId) {
    commit(types.default.SET_CURRENT_ACCOUNT_ID, accountId);
  },

  setCurrentUserAvailability({ commit, state: $state }, data) {
    if (data[$state.currentUser.id]) {
      commit(
        types.default.SET_CURRENT_USER_AVAILABILITY,
        data[$state.currentUser.id]
      );
    }
  },
};

// mutations
export const mutations = {
  [types.default.SET_CURRENT_USER_AVAILABILITY](_state, availability) {
    Vue.set(_state.currentUser, 'availability', availability);
  },
  [types.default.CLEAR_USER](_state) {
    _state.currentUser.id = null;
  },
  // [types.default.SET_CURRENT_USER](_state) {
  //   const currentUser = {
  //     ...authAPI.getAuthData(),
  //     ...authAPI.getCurrentUser(),
  //   };
  //
  //   Vue.set(_state, 'currentUser', currentUser);
  // },
  [types.default.SET_CURRENT_USER](_state, user) {
    const currentUser = {
      ...authAPI.getAuthData(),
      ...user,
    };
    Vue.set(_state, 'currentUser', currentUser);
  },
  [types.default.SET_CURRENT_USER_UI_SETTINGS](_state, { uiSettings }) {
    Vue.set(_state, 'currentUser', {
      ..._state.currentUser,
      ui_settings: {
        ..._state.currentUser.ui_settings,
        ...uiSettings,
      },
    });
  },
  [types.default.SET_CURRENT_ACCOUNT_ID](_state, accountId) {
    Vue.set(_state, 'currentAccountId', Number(accountId));
  },

  [types.default.SET_CODES](_state, codes) {
    Vue.set(_state, 'records', codes);
  }
};

export default {
  state,
  getters,
  actions,
  mutations,
};
